import { DEFAULT_COMPANY_COLORS } from '../../styles/colors'
import { ICompanyPure } from '../models/Company'
import { isCompanyRunning } from './isLeadOf'

// The eight things a freshly-created company still needs configured. Shared by the
// backend (to inject the dynamic COMPLETE_COMPANY_PROFILE request) and the frontend
// (to render the checklist + progress). Pure derivation from the company document —
// there is no stored progress, so completion always reflects the live data.
export type CompanySetupTaskId =
  | 'logo'
  | 'header'
  | 'branding'
  | 'tone'
  | 'mission'
  | 'dna'
  | 'framework'
  | 'skills'

export const COMPANY_SETUP_TASK_IDS: CompanySetupTaskId[] = [
  'logo',
  'header',
  'branding',
  'tone',
  'mission',
  'dna',
  'framework',
  'skills',
]

const sameTriple = (a?: number[], b?: number[]): boolean =>
  !!a && !!b && a[0] === b[0] && a[1] === b[1] && a[2] === b[2]

// Per-task completion. Mirrors the `buildMinimalCompany` defaults exactly (empty
// logo/background/skills/cultureValues/jobs, no aiContext), so a brand-new company
// has all eight open.
export const companySetupTaskDone = (
  company: ICompanyPure
): Record<CompanySetupTaskId, boolean> => {
  const ai = company.settings?.aiContext
  return {
    logo: !!company.logo,
    header: !!company.background,
    // Done once the UI "Main color" (text token) is customised away from default.
    branding:
      !!company.companyColors?.text &&
      !sameTriple(company.companyColors.text, DEFAULT_COMPANY_COLORS.text),
    tone: !!ai?.toneOfVoice,
    mission: !!ai?.missionStatement,
    dna: !!company.cultureValues?.length,
    framework: !!company.jobs?.length,
    skills: !!company.skills?.length,
  }
}

export const companySetupRemaining = (company: ICompanyPure): number => {
  const done = companySetupTaskDone(company)
  return COMPANY_SETUP_TASK_IDS.filter((id) => !done[id]).length
}

export const companySetupComplete = (company: ICompanyPure): boolean =>
  companySetupRemaining(company) === 0

// The checklist guides a company through its first setup. Once anyone other than
// support has logged in the company is in use, and established companies never had
// tone or mission, so the checklist would show as open on every one of them.
export const companySetupApplies = (company: ICompanyPure): boolean =>
  !isCompanyRunning(company)
